import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";
import { useState } from "react";
import type { JSX } from "react";
import { Link, useLocation } from "react-router";
import { useApiClient } from "../../api/ApiContext";
import { legalDocumentStatusSchema } from "../../api/schemas";
import type { LegalDocumentStatus } from "../../api/schemas";
import { routePaths } from "../../app/navigation";
import { hasAccessRight } from "../../shared/auth/accessRights";
import { useI18n } from "../../shared/i18n/I18nContext";
import type { MessageKey } from "../../shared/i18n/messages.de";
import { useMe } from "../../shared/session/useMe";
import { useAnnouncer } from "../../shared/ui/Announcer";
import { Alert } from "../../shared/ui/Alert";
import { Badge } from "../../shared/ui/Badge";
import { cx } from "../../shared/ui/classNames";
import { ConfirmAction } from "../../shared/ui/ConfirmAction";
import { ErrorMessage } from "../../shared/ui/ErrorMessage";
import { formatDate } from "../../shared/ui/format";
import { Skeleton } from "../../shared/ui/Skeleton";
import styles from "../../shared/ui/ui.module.css";
import local from "./Tenant.module.css";
import { readTenantSection, tenantSectionParameter, tenantSectionPath } from "./tenantSections";

/** Query key of the legal documents of the tenant. */
const legalDocumentsKey = ["tenant-legal-documents"] as const;

/** Prefix of the session storage key of the versions put off with „Später“, per tenant. */
const laterKeyPrefix = "timetracking.legalPromptLater.";

/** A decision of the tenant administrator. */
type LegalDecisionAction = "accept" | "decline";

/**
 * Reads the state of the legal documents of the tenant (T-130). Only people who may decide on them
 * (tenant administrators) load it.
 *
 * @param enabled - Whether the caller may decide.
 * @returns The query.
 */
export function useLegalDocuments(
  enabled: boolean,
): UseQueryResult<readonly LegalDocumentStatus[]> {
  const client = useApiClient();
  const query = useQuery({
    queryKey: legalDocumentsKey,
    queryFn: ({ signal }) =>
      client.get("/api/v1/tenant/legal-documents", legalDocumentStatusSchema.array(), { signal }),
    enabled,
  });

  return query;
}

/**
 * Returns the session storage, or `null` where the browser refuses it (private mode, sandbox).
 *
 * @returns The storage.
 */
function sessionStore(): Storage | null {
  let storage: Storage | null;

  try {
    storage = window.sessionStorage;
  } catch {
    storage = null;
  }

  return storage;
}

/**
 * Reads the document versions put off with „Später“ in this browser session (UX-001).
 *
 * @param storage - The session storage, or `null` without one.
 * @param tenantId - The tenant.
 * @returns The identifiers of the versions.
 */
export function readPutOff(storage: Pick<Storage, "getItem"> | null, tenantId: string): string[] {
  let ids: string[];

  try {
    const parsed: unknown = JSON.parse(storage?.getItem(laterKeyPrefix + tenantId) ?? "[]");
    ids = Array.isArray(parsed) ? parsed.filter((id) => typeof id === "string") : [];
  } catch {
    ids = [];
  }

  return ids;
}

/**
 * Remembers the document versions put off with „Später“ until the browser session ends; a new
 * version prompts again.
 *
 * @param storage - The session storage, or `null` without one.
 * @param tenantId - The tenant.
 * @param ids - The identifiers of the versions.
 */
export function writePutOff(
  storage: Pick<Storage, "setItem"> | null,
  tenantId: string,
  ids: readonly string[],
): void {
  try {
    storage?.setItem(laterKeyPrefix + tenantId, JSON.stringify(ids));
  } catch {
    // Kept in component state only.
  }
}

/**
 * Returns the message key of the name of a document kind.
 *
 * @param state - The document state.
 * @returns The message key.
 */
function kindKey(state: LegalDocumentStatus): MessageKey {
  const key: MessageKey = `legal.kind.${state.document.kind}`;
  return key;
}

/**
 * Returns the message key of the short name of a document kind, for example „AVV“.
 *
 * @param state - The document state.
 * @returns The message key.
 */
function shortKindKey(state: LegalDocumentStatus): MessageKey {
  const key: MessageKey = `legal.short.${state.document.kind}`;
  return key;
}

/** Properties of {@link LegalDocumentCard}. */
interface LegalDocumentCardProps {
  /** The document state. */
  readonly state: LegalDocumentStatus;
}

/**
 * One document with its version, the version in force and the decision buttons. Accepting and
 * declining are binding, so each asks for a confirmation that names the consequence (UX-113).
 *
 * @param props - Component properties.
 * @returns The card.
 */
function LegalDocumentCard(props: LegalDocumentCardProps): JSX.Element {
  const { t, languageTag } = useI18n();
  const { announce } = useAnnouncer();
  const client = useApiClient();
  const queryClient = useQueryClient();
  const { state } = props;
  const name = t(kindKey(state));
  const day = formatDate(state.document.validFrom, languageTag);
  const decide = useMutation({
    mutationFn: (action: LegalDecisionAction) =>
      client.post(
        `/api/v1/legal-documents/${encodeURIComponent(state.document.id)}:${action}`,
        undefined,
        legalDocumentStatusSchema,
      ),
    onSuccess: async (_, action) => {
      announce(
        t(action === "accept" ? "legal.acceptedAnnounce" : "legal.declinedAnnounce", {
          document: name,
          version: state.document.version,
        }),
      );
      await queryClient.invalidateQueries({ queryKey: legalDocumentsKey });
    },
  });
  const decisionText = !state.document.requiresAcceptance
    ? t("legal.informationOnly")
    : state.decision === "Accepted"
      ? t("legal.accepted")
      : state.decision === "Declined"
        ? t("legal.declined", { version: state.inForceVersion ?? "" })
        : state.inForceVersion === null
          ? t("legal.none")
          : t("legal.inForce", { version: state.inForceVersion });
  const mayDecline = state.canDecline && state.decision === null;

  const card = (
    <article className={cx(styles["card"], styles["stack"])} aria-label={name}>
      <h3>{name}</h3>
      <p>{t("legal.version", { version: state.document.version, day })}</p>
      <p>
        <a href={state.document.documentRef} target="_blank" rel="noopener noreferrer">
          {t("legal.open", { document: name })}
        </a>
      </p>
      {state.document.requiresAcceptance && state.decision === "Accepted" ? (
        <p>
          <Badge tone="success">{decisionText}</Badge>
        </p>
      ) : state.document.requiresAcceptance && state.decision === "Declined" ? (
        <Alert tone="danger" live="off">
          {decisionText}
        </Alert>
      ) : state.isPending ? (
        <Alert tone="warning" live="off">
          {decisionText}
        </Alert>
      ) : (
        <p className={styles["muted"]}>{decisionText}</p>
      )}
      {decide.isError ? <ErrorMessage error={decide.error} /> : null}
      {state.isPending || (state.decision === "Declined" && state.document.requiresAcceptance) ? (
        <div className={cx(styles["row"], local["actions"])}>
          <ConfirmAction
            label={t("legal.acceptShort")}
            accessibleLabel={t("legal.accept", { document: name })}
            className={styles["primary"]}
            tone="primary"
            consequence={t("legal.acceptConsequence", {
              document: name,
              version: state.document.version,
              day,
            })}
            confirmLabel={t("legal.acceptConfirm")}
            disabled={decide.isPending}
            onConfirm={() => {
              decide.mutate("accept");
            }}
          />
          {mayDecline ? (
            <ConfirmAction
              label={t("legal.declineShort")}
              accessibleLabel={t("legal.decline", { document: name })}
              consequence={t("legal.declineConsequence", {
                version: state.document.version,
                inForce: state.inForceVersion ?? "",
              })}
              confirmLabel={t("legal.declineConfirm")}
              disabled={decide.isPending}
              onConfirm={() => {
                decide.mutate("decline");
              }}
            />
          ) : null}
        </div>
      ) : null}
      {mayDecline ? <p className={styles["muted"]}>{t("legal.declineHint")}</p> : null}
    </article>
  );

  return card;
}

/**
 * Compact prompt above every page (T-130, UX-001): one line that names the document versions that
 * still need a decision, with a link to „Mandant → Vertrag und Export“. It never blocks the
 * application; clocking and exports keep working (spec 20.1 B-3). „Später“ hides the named versions
 * until the browser session ends; in „Vertrag und Export“ itself the prompt is not shown.
 *
 * @returns The prompt, or nothing when no decision is open.
 */
export function LegalDocumentsPrompt(): JSX.Element | null {
  const { t } = useI18n();
  const me = useMe();
  const location = useLocation();
  const tenantId = me.data?.tenantId ?? "";
  const mayDecide = hasAccessRight(me.data?.roles ?? [], "DecideLegalDocuments");
  const documents = useLegalDocuments(mayDecide);
  const [putOff, setPutOff] = useState<readonly string[] | null>(null);
  const later = putOff ?? readPutOff(sessionStore(), tenantId);
  const pending = (documents.data ?? []).filter(
    (state) => state.isPending && !later.includes(state.document.id),
  );
  const onContract =
    location.pathname === routePaths.tenant &&
    readTenantSection(new URLSearchParams(location.search).get(tenantSectionParameter)) ===
      "contract";

  if (!mayDecide || onContract || pending.length === 0) {
    return null;
  }

  const prompt = (
    <section
      aria-label={t("legal.promptTitle")}
      className={cx(styles["alert"], styles["alertInfo"], local["prompt"])}
    >
      <p>
        {t("legal.promptLine", {
          count: pending.length,
          documents: pending.map((state) => t(shortKindKey(state))).join(", "),
        })}
      </p>
      <Link className={styles["linkButton"]} to={tenantSectionPath("contract")}>
        {t("legal.review")}
      </Link>
      <button
        type="button"
        className={styles["linkButton"]}
        onClick={() => {
          const ids = [...later, ...pending.map((state) => state.document.id)];
          writePutOff(sessionStore(), tenantId, ids);
          setPutOff(ids);
        }}
      >
        {t("legal.later")}
      </button>
    </section>
  );

  return prompt;
}

/**
 * All legal documents of the tenant with the version in force (T-130), in „Mandant → Vertrag und
 * Export“.
 *
 * @returns The section.
 */
export function LegalDocumentsSection(): JSX.Element {
  const { t } = useI18n();
  const documents = useLegalDocuments(true);
  const hasPending = (documents.data ?? []).some((state) => state.isPending);

  const section = (
    <section aria-labelledby="legal-title" className={styles["stack"]}>
      <h2 id="legal-title">{t("legal.title")}</h2>
      <p className={styles["muted"]}>{t("legal.intro")}</p>
      {hasPending ? <p>{t("legal.prompt")}</p> : null}
      {documents.isPending ? <Skeleton shape="card" /> : null}
      {documents.isError ? (
        <ErrorMessage
          error={documents.error}
          retrying={documents.isFetching}
          onRetry={() => {
            void documents.refetch();
          }}
        />
      ) : null}
      {documents.data?.length === 0 ? <p>{t("legal.empty")}</p> : null}
      {(documents.data ?? []).map((state) => (
        <LegalDocumentCard key={state.document.id} state={state} />
      ))}
    </section>
  );

  return section;
}
